import React, { useEffect, useRef, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { Button, TextInput } from "@jugl-web/ui-components/cross-platform";
import { SubmitHandler, useForm } from "react-hook-form";
import { usersApi } from "@web-src/features/api/createApi";
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "@jugl-web/utils/consts";
import { saveItemToStorage, SHOW_SIGN_IN_FLOW } from "@jugl-web/utils/storage";
import { useEntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { SignUpFlowLayout } from "../SignUpFlowLayout";
import { SignUpFlowContentLayout } from "../../components/SignUpFlowContentLayout/SignUpFlowContentLayout";
import { useUserAuth } from "../../providers/UserAuthProvider/useUserAuth";
import { AuthDiscardConfirmationAlert } from "../../components/AuthDiscardConfirmationAlert/AuthDiscardConfirmationAlert";
import { MAX_NAME_LENGTH } from "../../conts";

type FormValues = {
  firstName: string;
  lastName: string;
  avatar?: File;
  consent: boolean;
};

export const ProfileForm: React.FC<{
  isInviteLink?: boolean;
}> = ({ isInviteLink }) => {
  const { t } = useTranslations();
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    mode: "onChange",
  });
  const { me, isLoading } = useMe();
  const isInitialized = useRef(false);
  const { navigateToPage } = useNavigation();
  useEffect(() => {
    if (isInitialized.current) {
      return;
    }
    if (me && !isLoading) {
      isInitialized.current = true;
      if (me.profile) {
        navigateToPage("entryPoint");
      }
    }
  }, [me, isLoading, navigateToPage]);

  const [isDiscardAlertOpen, setIsDiscardAlertOpen] = useState(false);
  const { logout } = useUserAuth();

  const [createProfile, { isLoading: isCreateLoading }] =
    usersApi.useCreateProfileMutation();
  const {
    entityInvitesState: { entityInvites },
  } = useEntitiesListProvider();
  const onSubmit: SubmitHandler<FormValues> = async (params) => {
    await createProfile({
      first_name: params.firstName,
      last_name: params.lastName,
    });

    if (isInviteLink) {
      return;
    }

    saveItemToStorage(SHOW_SIGN_IN_FLOW, true);

    if (entityInvites?.length) {
      navigateToPage("authInvitations");
    } else {
      navigateToPage("authSignUpPurpose");
    }

    saveItemToStorage(SHOW_SIGN_IN_FLOW, false);
  };

  return (
    <>
      <AuthDiscardConfirmationAlert
        isOpen={Boolean(isDiscardAlertOpen)}
        variant="profile"
        onConfirm={() => {
          logout(false);
        }}
        onClose={() => setIsDiscardAlertOpen(false)}
      />
      <SignUpFlowLayout
        type="profileForm"
        progressBar={17}
        footerClassName="pb-6"
        onBackClick={() => {
          setIsDiscardAlertOpen(true);
        }}
      >
        <SignUpFlowContentLayout
          title={t({
            id: "profile-form-page.hi-there-whats-your-name?",
            defaultMessage: "Hi there! What’s your name?",
          })}
          subtitle={t({
            id: "profile-form-page.create-account-provide-info-subtitle",
            defaultMessage: "Just a few quick details to kick things off 🎉",
          })}
          description={
            <form
              className="font-secondary flex h-full w-full flex-col items-center"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="flex w-[400px] flex-col items-center justify-center gap-8">
                <TextInput
                  classNames={{
                    wrapperClassName: "w-full",
                  }}
                  autoFocus
                  label={t({
                    id: "common.first-name",
                    defaultMessage: "First Name",
                  })}
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  isRequired
                  {...register("firstName", {
                    required: true,
                    validate: (value) => !!value.trim(),
                    maxLength: {
                      value: MAX_NAME_LENGTH,
                      message: `Maximum character limit reached (${MAX_NAME_LENGTH})`,
                    },
                  })}
                  isInvalid={!!errors.firstName}
                  errorMessage={errors.firstName?.message}
                />
                <TextInput
                  classNames={{
                    wrapperClassName: "w-full",
                  }}
                  label={t({
                    id: "common.last-name",
                    defaultMessage: "Last Name",
                  })}
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  isRequired
                  {...register("lastName", {
                    required: true,
                    validate: (value) => !!value.trim(),
                    maxLength: {
                      value: MAX_NAME_LENGTH,
                      message: `Maximum character limit reached (${MAX_NAME_LENGTH})`,
                    },
                  })}
                  isInvalid={!!errors.lastName}
                  errorMessage={errors.lastName?.message}
                />
              </div>
              <div className="flex flex-col items-center pt-[32px] 2xl:pt-[64px]">
                <Button
                  className="disabled:bg-grey-200 disabled:text-dark-600 disabled:hover:bg-grey-200 mx-auto w-[300px]"
                  type="submit"
                  size="small"
                  isDisabled={!isValid || isCreateLoading}
                >
                  {t({
                    id: "common.continue",
                    defaultMessage: "Continue",
                  })}
                </Button>
                <div className="font-secondary text-dark-500 w-[300px] pt-5 text-center text-sm leading-[160%]">
                  You are agreeing to our{" "}
                  <a
                    className="text-primary-600"
                    href={TERMS_OF_USE_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms and Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    className="text-primary-600"
                    href={PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>
            </form>
          }
        />
      </SignUpFlowLayout>
    </>
  );
};
